import { supabase } from "@/integrations/supabase/client";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export type WebPushStatus = "checking" | "unsupported" | "disabled" | "denied" | "enabled";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((character) => character.charCodeAt(0)));
}

function isWebPushSupported() {
  return typeof window !== "undefined"
    && typeof Notification !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window;
}

export async function getWebPushStatus(): Promise<WebPushStatus> {
  if (!isWebPushSupported()) {
    return "unsupported";
  }

  if (Notification.permission === "denied") {
    return "denied";
  }

  if (Notification.permission !== "granted") {
    return "disabled";
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription ? "enabled" : "disabled";
}

export async function enableWebPushNotifications(userId: string): Promise<WebPushStatus> {
  if (!isWebPushSupported()) {
    return "unsupported";
  }

  if (!VAPID_PUBLIC_KEY) {
    throw new Error("Web push is not configured yet.");
  }

  const permission = Notification.permission === "granted"
    ? "granted"
    : await Notification.requestPermission();

  if (permission !== "granted") {
    return permission === "denied" ? "denied" : "disabled";
  }

  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  const { error } = await supabase.from("push_subscriptions" as any).upsert(
    {
      user_id: userId,
      subscription: subscription.toJSON(),
    } as any,
    {
      onConflict: "user_id,subscription",
      ignoreDuplicates: false,
    },
  );

  if (error) {
    throw error;
  }

  return "enabled";
}
